/**
 * 言語切替ストア
 *
 * アプリケーション全体の言語設定を管理するZustandストア。
 * ローカルストレージと連携し、ユーザーの言語設定を永続化します。
 *
 * @see docs/system.md
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SupportedLanguage } from '@/lib/i18n-helpers';

/**
 * 言語ストアの状態型
 */
interface LanguageState {
  /** 現在の言語設定 */
  language: SupportedLanguage;

  /**
   * 言語を変更する
   * @param lang - 設定する言語コード
   */
  setLanguage: (lang: SupportedLanguage) => void;
}

/**
 * 言語設定ストア
 *
 * 使用例:
 * ```typescript
 * import { useLanguageStore } from '@/stores/language-store';
 *
 * function MyComponent() {
 *   const { language, setLanguage } = useLanguageStore();
 *
 *   return (
 *     <button onClick={() => setLanguage(language === 'ja' ? 'en' : 'ja')}>
 *       {language === 'ja' ? 'English' : '日本語'}
 *     </button>
 *   );
 * }
 * ```
 */
export const useLanguageStore = create<LanguageState>()(
  persist(
    (set) => ({
      // 初期値: 日本語
      language: 'ja',

      // 言語変更アクション（純粋な状態更新のみ。DOM反映は App.tsx の useEffect で行う）
      setLanguage: (lang) => set({ language: lang }),
    }),
    {
      // ローカルストレージのキー名
      name: 'codex-language-storage',
    }
  )
);
